import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
    DEFAULT_GDPR_COOKIE_VALUE,
    EU_COUNTRIES,
    IMG_COUNTRIES,
    NA_COUNTRIES,
    ONE_TRUST_COUNTRIES,
    PERSON_ICON_COUNTRIES,
    SA_COUNTRIES,
    SESSION_KEYS,
    SUPPRESS_US_COOKIE_BANNER_COUNTRIES,
    SYNDICATED_HEADER_COUNTRIES,
} from '@constants';
import { findPathByAlias, RoutesList } from '@routes/routesList';
import AuthenticationService from '@services/authentication-service/authentication-service';
import AppConfigurationService from '@services/app-configuration-service/app-configuration-service';
import { isWebviewFromSearch } from '@services/authentication-service/utils/is-webview';
import { scriptService } from '@services/script-service/script-service';
import ServerContext from '@contexts/serverContext';
import NotificationProvider from '@contexts/notificationContext';
import { EuHeader, Footer, GdprBanner } from '@sections/index';
import { NotificationBanner } from '@sections/notification-banner/notification-banner';
import { remapDragonflySyndicatedHeaderLinks } from '@utils/dragonfly-syndicated-header-links';
import './App.scss';
import ModalProvider from './contexts/modalContext';
import HelpRouterContainer from '@sections/help-router/help-router-container';
import serverSideService from '@services/server-side-service/server-side-service';
import SideDrawerProvider from '@contexts/sideDrawerContext';
import ConnectedStatusProvider from '@contexts/connectedStatusContext';
import { interceptCrossDomainLinkBehavior } from '@utils/intercept-cross-domain-link-behavior';
import { consentService } from '@services/consent-service/consent-service';
import ZoomResetUtil from './components/utils/zoom-reset/zoom-reset';

interface Props {
    serverContext?: any;
    serverSideService: any;
}

function App(props: Props) {
    const currentAppConfiguration = useMemo(
        () => new AppConfigurationService(),
        []
    );
    const authenticationService = useMemo(
        () => new AuthenticationService(),
        []
    );

    const appConfig = useMemo(
        () =>
            props.serverContext
                ? {
                      brand: props.serverContext.brand,
                      languageRegionCode:
                          props.serverContext.currentLanguageRegionCode,
                      countryCode: props.serverContext.currentRegionCode,
                      domain: currentAppConfiguration.getAppConfiguration()
                          .domain,
                  }
                : {
                      brand: currentAppConfiguration.getAppConfiguration()
                          .brand,
                      languageRegionCode:
                          currentAppConfiguration.getAppConfiguration()
                              .languageRegionCode,
                      countryCode:
                          currentAppConfiguration.getAppConfiguration()
                              .countryCode,
                      domain: currentAppConfiguration.getAppConfiguration()
                          .domain,
                  },
        [props.serverContext, currentAppConfiguration]
    );

    const [brand, language, country, domain] = [
        appConfig.brand,
        appConfig.languageRegionCode,
        appConfig.countryCode,
        appConfig.domain,
    ];

    const regionChecks = useMemo(
        () => ({
            isEu: EU_COUNTRIES.includes(country),
            isNotNa: PERSON_ICON_COUNTRIES.includes(country),
            isImg: IMG_COUNTRIES.includes(country),
        }),
        [country]
    );

    const { isEu, isNotNa, isImg } = regionChecks;

    const navigate = useNavigate();
    const context = props.serverSideService.isServerSide()
        ? props.serverContext
        : (window as any).SERVER_CONTEXT;
    const location = useLocation();
    const fmaState: string = authenticationService.getState();
    const isOnSignInCredentialsPage: boolean = location.pathname.includes(
        '/signin-credentials'
    );
    const isOnPreferredDealerPage: boolean =
        location.pathname.includes('/preferred-dealer');
    const fullPathname = location.pathname + (location.search || '');
    const isOnIDPSignInPage: boolean =
        location.pathname.includes('signin-credentials');
    const isWebview: boolean = useMemo(
        () => isWebviewFromSearch(location.search),
        [location.search]
    );
    const isWebviewSignInCredentials = isWebview && isOnSignInCredentialsPage;
    const showHelpRouter =
        !isOnIDPSignInPage && !isOnPreferredDealerPage && !isWebview;
    const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);

    if (
        serverSideService.isClientSide() &&
        !localStorage.getItem('ccpaConsentValue')
    ) {
        localStorage.setItem(
            'ccpaConsentValue',
            consentService.nonEssentialScriptsAllowed().toString()
        );
    }

    const syndicatedHeaderLoaded = () => {
        return SYNDICATED_HEADER_COUNTRIES.includes(context.currentRegionCode);
    };

    const hideHeaderAndFooter = (): void => {
        const headerAndFooter: NodeListOf<Element> = document.querySelectorAll(
            'header, footer, .eu-header, .eu-footer, #global-ux, .footer, .fgx-pantry-bootstrap'
        );
        headerAndFooter.forEach((element: HTMLElement) => {
            if (element && element.style.display !== 'none') {
                element.style.display = 'none';
            }
        });
    };

    const showHeader = (): void => {
        const headerAndFooter: NodeListOf<Element> =
            document.querySelectorAll('header,.eu-header');
        headerAndFooter.forEach((element: HTMLElement) => {
            if (element && element.style.display === 'none') {
                element.style.display = 'block';
            }
        });
    };

    useEffect(() => {
        if (location) {
            if (
                ['mx', ...IMG_COUNTRIES, ...SA_COUNTRIES].includes(
                    currentAppConfiguration.getAppConfiguration().countryCode
                )
            ) {
                remapDragonflySyndicatedHeaderLinks(
                    language,
                    currentAppConfiguration.currentDomain,
                    currentAppConfiguration.getPathname(),
                    location.search
                );
            }

            if (['au', 'nz'].includes(country)) {
                return interceptCrossDomainLinkBehavior(domain, fullPathname);
            }
        }
    }, [fullPathname, location.search]);

    useEffect(() => {
        if (
            appConfig.languageRegionCode === 'fr-ca' &&
            document.documentElement.lang !== 'fr'
        ) {
            document.documentElement.lang = 'fr';
        }
    }, [appConfig.languageRegionCode]);

    const hideUSBannerCheck = (): void => {
        const queryParams = new URLSearchParams(location.search);
        let localeParam = '';
        const hideBanner = 'hideUSCookieBanner';
        if (
            country === 'us' &&
            window.location.href.includes('/signin-credentials')
        ) {
            localeParam = queryParams.get('locale') || '';
            if (SUPPRESS_US_COOKIE_BANNER_COUNTRIES.includes(localeParam)) {
                localStorage.setItem(hideBanner, localeParam);
            }
        }
    };

    const clearHideUSBanner = (): void => {
        const hideBanner = 'hideUSCookieBanner';
        if (
            SUPPRESS_US_COOKIE_BANNER_COUNTRIES.includes(
                localStorage.getItem(hideBanner)
            )
        ) {
            if (
                !window.location.href.includes(
                    'locale=' + localStorage.getItem(hideBanner)
                ) &&
                window.location.pathname !== '/myaccount/' &&
                window.location.pathname !== '/'
            ) {
                localStorage.removeItem(hideBanner);
            }
        }
    };

    const syndicatedSignOutHandler = useCallback((e: MouseEvent) => {
        e.preventDefault();
        authenticationService.logout();
    }, []);

    const addSignOutListeners = useCallback(
        (links: NodeListOf<HTMLAnchorElement>) => {
            links.forEach((link) =>
                link.addEventListener('click', syndicatedSignOutHandler)
            );
        },
        [syndicatedSignOutHandler]
    );

    const removeSignOutListeners = useCallback(
        (links: NodeListOf<HTMLAnchorElement>) => {
            links.forEach((link) =>
                link.removeEventListener('click', syndicatedSignOutHandler)
            );
        },
        [syndicatedSignOutHandler]
    );

    useEffect(() => {
        const syndicatedHeaderSignOutLinks: NodeListOf<HTMLAnchorElement> =
            document.querySelectorAll(
                'a[href*="#$userSignOut"], li.fma-signout a'
            );

        let hasNotConsentedToCookies: boolean =
            isWebview ||
            (NA_COUNTRIES.includes(appConfig.countryCode) &&
                localStorage.getItem('ccpaConsentValue') !== 'true');

        if (appConfig.countryCode === 'ca') {
            hasNotConsentedToCookies = true;
        }

        scriptService.loadAnalyticsScript(hasNotConsentedToCookies);
        scriptService.loadDynatraceTags(hasNotConsentedToCookies);
        scriptService.loadConnectedServicesScript();
        if (country === 'us') {
            scriptService.loadOneLinkTranslationScript();
        }
        hideUSBannerCheck();
        clearHideUSBanner();

        if (
            !isWebview &&
            ONE_TRUST_COUNTRIES.includes(country) &&
            !SUPPRESS_US_COOKIE_BANNER_COUNTRIES.includes(
                localStorage.getItem('hideUSCookieBanner')
            )
        ) {
            scriptService.loadOneTrustCookieBanner();
        }

        // Reloading FMA while an auth transaction is completing (OAuth callback
        // or a passkey/MFA return) discards the in-memory PKCE code_verifier
        // (FMA-AUTH-0004-E redirect loop) and recomputes data-state to the
        // /account-dashboard fallback. Keep the existing instance in that case.
        // location.search is sampled (not a dep) on purpose: transaction returns
        // are full-page loads, so this effect already runs fresh with the right
        // URL, and we must NOT reload FMA on in-app query-only changes.
        const authTxnParams = new URLSearchParams(location.search);
        const hasActiveAuthTransaction =
            authTxnParams.has('code') ||
            authTxnParams.has('state') ||
            authTxnParams.has('passkeyAdd') ||
            authTxnParams.has('passkeyRemove');
        const fmaAlreadyLoaded = !!document.body.querySelector(
            'script[data-fma-script]'
        );

        if (!(fmaAlreadyLoaded && hasActiveAuthTransaction)) {
            scriptService.unloadFMA();
            scriptService.loadFMA();
        }

        if (syndicatedHeaderLoaded()) {
            addSignOutListeners(syndicatedHeaderSignOutLinks);
            return () => removeSignOutListeners(syndicatedHeaderSignOutLinks);
        }
    }, [language, isWebviewSignInCredentials]);

    function addDashboardPath() {
        return window.location.href.endsWith('/')
            ? window.location.href.concat('account-dashboard')
            : window.location.href.concat('/account-dashboard');
    }

    useEffect(() => {
        authenticationService.onIsAuthenticated().then((authenticated) => {
            const pathName = location.pathname.endsWith('/')
                ? location.pathname
                : location.pathname + '/';

            !authenticationService.getFmaState() &&
                authenticationService.setFmaState();

            authenticationService.setFmaRedirectUrl(
                pathName ===
                    currentAppConfiguration.getAppConfiguration().root ||
                    location.pathname === '/'
                    ? addDashboardPath()
                    : window.location.href
            );

            const newCurrentAppConfiguration =
                currentAppConfiguration.getAppConfiguration();

            const simplifiedUrlPathname = `/${newCurrentAppConfiguration.brand}/${newCurrentAppConfiguration.languageRegionCode}/`;

            if (authenticated) {
                const pathnameWithSlash = window.location.pathname.endsWith('/')
                    ? window.location.pathname
                    : window.location.pathname + '/';

                if (pathnameWithSlash === simplifiedUrlPathname) {
                    window.location.pathname.endsWith('/')
                        ? (window.location.pathname =
                              window.location.pathname + 'account-dashboard')
                        : (window.location.pathname =
                              window.location.pathname + '/account-dashboard');
                }

                const isReturningFromPasskeyFlow = sessionStorage.getItem(
                    SESSION_KEYS.PASSKEY_FLOW_RETURN
                );

                if (
                    !sessionStorage.getItem('postLoginNavComplete') ||
                    isReturningFromPasskeyFlow
                ) {
                    setIsAuthenticated(true);

                    if (authenticationService.getFmaState()) {
                        const state = authenticationService.getFmaState();
                        navigate(state?.startsWith('/') ? state : `/${state}`);
                    } else if (!authenticationService.getFmaState()) {
                        if (location.pathname === '/')
                            navigate(findPathByAlias('AccountDashboardView'));
                    }

                    sessionStorage.setItem('postLoginNavComplete', 'true');
                }
            }
        });
    }, [location]);

    useEffect(() => {
        if (
            !serverSideService.isClientSide() ||
            !NA_COUNTRIES.includes(country)
        ) {
            return;
        }

        const syncCcpaConsent = (): void => {
            const nonEssentialScriptConsent = consentService
                .nonEssentialScriptsAllowed()
                .toString();

            const current = localStorage.getItem('ccpaConsentValue');
            if (current !== nonEssentialScriptConsent) {
                localStorage.setItem(
                    'ccpaConsentValue',
                    nonEssentialScriptConsent
                );
                window.location.reload();
            }
        };

        if (appConfig.countryCode === 'us') syncCcpaConsent();

        const handler = () => {
            syncCcpaConsent();
        };

        window.addEventListener('OneTrustGroupsUpdated', handler);

        return () => {
            window.removeEventListener('OneTrustGroupsUpdated', handler);
        };
    }, [country]);

    useEffect(() => {
        const portal = document.getElementById('portal');

        if (brand && portal) {
            portal.className = `brand-${brand}`;
            portal.setAttribute('data-brand', brand);
        }
    }, []);

    useEffect(() => {
        const sideDrawerPortal = document.getElementById('side-drawer-portal');

        if (brand && sideDrawerPortal) {
            sideDrawerPortal.className = `brand-${brand}`;
            sideDrawerPortal.setAttribute('data-brand', brand);
        }
    }, []);

    useEffect(() => {
        if (isOnSignInCredentialsPage || isWebview) {
            hideHeaderAndFooter();
        } else if (!isOnSignInCredentialsPage && !isWebview) {
            showHeader();
        }
        clearHideUSBanner();
    }, [location.pathname]);

    useEffect(() => {
        if (fmaState?.includes('webview')) {
            hideHeaderAndFooter();
        }
    }, [fmaState]);

    useEffect(() => {
        if (serverSideService.isClientSide() && isEu) {
            (window as any).digitaldata = {
                page: { country: `ford-${country}` },
                user: {
                    optInStatus: DEFAULT_GDPR_COOKIE_VALUE,
                },
            };
        }
    }, []);

    const getBrandHeader = () => {
        if (isOnSignInCredentialsPage || isWebview) return null;

        if (syndicatedHeaderLoaded()) return <></>;

        if (EU_COUNTRIES.includes(country)) return <EuHeader />;

        return null;
    };
    const getBrandFooter = () => {
        if (isOnSignInCredentialsPage || isWebview) return null;
        if (syndicatedHeaderLoaded()) return <></>;
        const footerRenderer = <Footer />;
        return footerRenderer;
    };
    const loadGDPRBanner = (): boolean => {
        if (isWebview || isOnSignInCredentialsPage) return false;
        const isSweden =
            currentAppConfiguration.getAppConfiguration().countryCode === 'se';
        return EU_COUNTRIES.includes(country) && !isSweden;
    };

    const changeImgSyndicatedHeaderClass = (): void => {
        const imgSyndicatedHeader =
            document.querySelector('#global-ux .header');
        const brandLogoContainer = document.querySelector(
            '.navigation_item-center'
        );
        const isProd =
            process.env.REACT_APP_ACTIVE_PROFILE &&
            process.env.REACT_APP_ACTIVE_PROFILE === 'prod';

        if (imgSyndicatedHeader) {
            imgSyndicatedHeader.classList?.replace('header', 'fixed-header');

            if (
                !brandLogoContainer?.contains(
                    document.querySelector('.logoImg')
                ) &&
                !isProd &&
                ['vn', 'th'].includes(country)
            ) {
                const country =
                    currentAppConfiguration.getAppConfiguration().countryCode;
                const languageRegionCode = currentAppConfiguration
                    .getAppConfiguration()
                    .languageRegionCode.replace('-', '_');
                const brandLogo = document.createElement('a');

                brandLogo.href = `/content/ford/${country}/${languageRegionCode}/dxdhome.html`;
                brandLogo.className = 'logoImg';

                brandLogoContainer?.appendChild(brandLogo);
            }
        }
    };

    useEffect(() => {
        IMG_COUNTRIES.includes(country) && changeImgSyndicatedHeaderClass();

        if (NA_COUNTRIES.includes(country)) {
            const naHeader = document.querySelector(
                'header.fgx-pantry-bootstrap'
            );
            const syndicatedAltHeader = document.querySelector(
                '.syndicated-alt-header'
            );

            if (
                naHeader &&
                naHeader instanceof HTMLElement &&
                !syndicatedAltHeader
            ) {
                naHeader.style.height = 'unset';
            }
        }
    }, []);

    useEffect(() => {
        if (serverSideService.isClientSide()) {
            new ZoomResetUtil().resetZoom();
        }
    }, [location]);

    return (
        <ServerContext.Provider value={context}>
            <div
                className={`brand-${brand} ${isEu ? 'eu' : ''} ${
                    isImg ? 'img' : ''
                } ${isNotNa ? 'not-na' : 'na'}`}
                data-brand={brand}
                data-testid="app"
            >
                <ConnectedStatusProvider>
                    <ModalProvider>
                        <SideDrawerProvider>
                            {getBrandHeader()}

                            {showHelpRouter && (
                                <HelpRouterContainer
                                    language={language}
                                    country={country}
                                />
                            )}

                            <NotificationProvider>
                                <NotificationBanner
                                    type={null}
                                    isAuthenticated={isAuthenticated}
                                />

                                <div className="main-overlay" />
                                <main className="app">
                                    <RoutesList />
                                </main>
                            </NotificationProvider>
                            {getBrandFooter()}
                            {loadGDPRBanner() && <GdprBanner />}
                        </SideDrawerProvider>
                    </ModalProvider>
                </ConnectedStatusProvider>
            </div>
        </ServerContext.Provider>
    );
}

export default App;
